import type { TourRouteCoordinate } from '@/features/tour/tour-route-map-utils'
import type { ITourTranslation, TourBasicInfoState } from '@/shared/api/types/tour-types'
import type { TourValidationError } from './types'
import { MIN_TOUR_DURATION_MINUTES, TOUR_FORM_STEP_IDS } from '@/shared/constants/tour-form'
import { normalizeTourTranslations } from './payload'
import {
  validateRouteCoordinates,
  validateTicketLink,
  validateTourImages,
  validateTourScope,
  validateTourTranslations,
} from './validation'

export type TourFormStepId = (typeof TOUR_FORM_STEP_IDS)[number]

/** `null` means the step is complete; otherwise the first problem, which doubles as the hint shown to the user. */
export type TourStepErrors = Record<TourFormStepId, TourValidationError | null>

export interface TourStepInput {
  translations: ITourTranslation[]
  basicInfo: TourBasicInfoState
  /** Final route coordinates (manual edit, or inferred from the selected POIs). */
  coordinates: TourRouteCoordinate[]
  banner?: string | undefined
  thumbnail?: string | undefined
  isUploading: boolean
}

/** Per-step completeness, using the same rules as the final submit so "next" never promises more than "create" accepts. */
export function getTourStepErrors(input: TourStepInput): TourStepErrors {
  const { translations, basicInfo, coordinates, banner, thumbnail, isUploading } = input
  return {
    content:
      validateTourTranslations(normalizeTourTranslations(translations)) ??
      (basicInfo.duration < MIN_TOUR_DURATION_MINUTES ? { key: 'tour.durationMin30' } : null) ??
      validateTicketLink(basicInfo.ticketLink),
    scope: validateTourScope(basicInfo),
    route: validateRouteCoordinates(coordinates),
    media: isUploading ? { key: 'tour.waitMediaUpload' } : validateTourImages(banner, thumbnail),
  }
}

/**
 * VI: Khi tạo mới, bước sau chỉ mở khi mọi bước trước đã đủ dữ liệu (POI phụ thuộc phạm vi); khi sửa thì mở tự do vì dữ liệu đã có sẵn.
 * EN: On create a step opens only once every earlier step is complete (POIs depend on the scope); on edit all steps are open because the data already exists.
 */
export function isTourStepUnlocked(
  stepId: TourFormStepId,
  errors: TourStepErrors,
  isEdit: boolean,
): boolean {
  if (isEdit) return true
  const index = TOUR_FORM_STEP_IDS.indexOf(stepId)
  return TOUR_FORM_STEP_IDS.slice(0, index).every((id) => errors[id] === null)
}
